/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

// ============================================================================
// Types
// ============================================================================

/**
 * Focus mode context value exposed to consumers
 * Matches the existing UseFocusModeReturn interface for backward compatibility
 */
export interface FocusModeContextValue {
  /** Whether focus mode is currently enabled */
  isFocusMode: boolean;
  /** Toggle focus mode on/off */
  toggleFocusMode: () => void;
  /** Enable focus mode */
  enableFocusMode: () => void;
  /** Disable focus mode */
  disableFocusMode: () => void;
}

/**
 * Props for the FocusModeProvider component
 */
export interface FocusModeProviderProps {
  children: React.ReactNode;
  /** Optional initial value (primarily for testing) */
  defaultValue?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * localStorage key for persisting focus mode preference
 * Must match the key used in the legacy use-focus-mode.ts hook
 */
const FOCUS_MODE_STORAGE_KEY = 'propel_focus_mode_enabled';

// ============================================================================
// Context
// ============================================================================

/**
 * Context for focus mode state
 * Initialized with null to detect missing provider (same pattern as SidebarContext)
 */
const FocusModeContext = createContext<FocusModeContextValue | null>(null);
FocusModeContext.displayName = 'FocusModeContext';

// ============================================================================
// Utilities
// ============================================================================

/**
 * Safely get initial focus mode state from localStorage
 */
const getInitialState = (defaultValue?: boolean): boolean => {
  if (typeof window === 'undefined') {
    return defaultValue ?? false;
  }

  try {
    const stored = window.localStorage.getItem(FOCUS_MODE_STORAGE_KEY);
    if (stored === null) {
      return defaultValue ?? false;
    }
    return JSON.parse(stored) === true;
  } catch {
    return defaultValue ?? false;
  }
};

/**
 * Safely persist focus mode state to localStorage
 */
const persistState = (value: boolean): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(FOCUS_MODE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    console.warn('Failed to persist focus mode preference to localStorage');
  }
};

// ============================================================================
// Provider Component
// ============================================================================

/**
 * FocusModeProvider - Provides shared focus mode state to workshop components
 *
 * This provider should wrap workshop routes to enable focus mode state sharing
 * between WorkshopHeader (controls) and WorkshopDetail (visual changes).
 *
 * Features:
 * - SSR-safe localStorage initialization
 * - Automatic state persistence
 * - Stable callback references (memoized)
 * - Compatible with existing use-focus-mode.ts tests
 *
 * @example
 * ```tsx
 * <FocusModeProvider>
 *   <WorkshopDetail />
 * </FocusModeProvider>
 * ```
 */
export function FocusModeProvider({
  children,
  defaultValue,
}: FocusModeProviderProps) {
  const [isFocusMode, setIsFocusMode] = useState<boolean>(() =>
    getInitialState(defaultValue)
  );

  // Sync state changes to localStorage
  useEffect(() => {
    persistState(isFocusMode);
  }, [isFocusMode]);

  // Stable callback to toggle focus mode
  const toggleFocusMode = useCallback(() => {
    setIsFocusMode((prev) => !prev);
  }, []);

  // Stable callback to enable focus mode
  const enableFocusMode = useCallback(() => {
    setIsFocusMode(true);
  }, []);

  // Stable callback to disable focus mode
  const disableFocusMode = useCallback(() => {
    setIsFocusMode(false);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<FocusModeContextValue>(
    () => ({
      isFocusMode,
      toggleFocusMode,
      enableFocusMode,
      disableFocusMode,
    }),
    [isFocusMode, toggleFocusMode, enableFocusMode, disableFocusMode]
  );

  return (
    <FocusModeContext.Provider value={contextValue}>
      {children}
    </FocusModeContext.Provider>
  );
}
FocusModeProvider.displayName = 'FocusModeProvider';

// ============================================================================
// Consumer Hook
// ============================================================================

/**
 * useFocusMode - Hook for consuming focus mode context
 *
 * Must be used within a FocusModeProvider. Throws an error if used outside
 * the provider tree (matches SidebarContext pattern).
 *
 * @returns Focus mode state and control functions
 * @throws Error if used outside FocusModeProvider
 *
 * @example
 * ```tsx
 * const { isFocusMode, toggleFocusMode } = useFocusMode();
 * ```
 */
export function useFocusMode(): FocusModeContextValue {
  const context = useContext(FocusModeContext);

  if (!context) {
    throw new Error(
      'useFocusMode must be used within a FocusModeProvider. ' +
        'Wrap your workshop routes with <FocusModeProvider>.'
    );
  }

  return context;
}

/**
 * Optional focus mode hook that returns null when outside FocusModeProvider
 * Use this in components that may or may not be within the provider
 *
 * @example
 * ```tsx
 * const focusMode = useFocusModeOptional();
 * const isFocusMode = focusMode?.isFocusMode ?? false;
 * ```
 */
export function useFocusModeOptional(): FocusModeContextValue | null {
  return useContext(FocusModeContext);
}
